import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SearchSelector from 'legacyfrontend/components/home/components/SearchSelector';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { useCacheConfig } from 'legacyfrontend/contexts/CacheConfigContext';
import { Seo } from 'core/components/seo';
import { useAppName } from 'legacyfrontend/hooks/useSafeState';
import { HotelResultsFromUrl } from 'frontend/features/hotels';
import { selectIsHotelSearcherEnabled } from 'legacyfrontend/redux/selectors/application-selectors';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';

const buildSeoFromParams = (search, appName) => {
  if (!search) {
    return {
      title: `Buscar Hoteles | ${appName}`,
      description: `Encontrá los mejores hoteles en ${appName}. Compará precios, ubicaciones y servicios. Reservá online.`,
    };
  }

  const params = new URLSearchParams(search);
  const destinationName = params.get('destination');
  const checkInDate = params.get('checkIn');
  const checkOutDate = params.get('checkOut');

  if (destinationName && checkInDate && checkOutDate) {
    const checkIn = new Date(checkInDate + 'T00:00:00');
    const checkOut = new Date(checkOutDate + 'T00:00:00');
    const monthNames = [
      '', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
    ];
    const inDay = checkIn.getDate();
    const inMonth = monthNames[checkIn.getMonth() + 1] || checkIn.getMonth() + 1;
    const outDay = checkOut.getDate();
    const outMonth = monthNames[checkOut.getMonth() + 1] || checkOut.getMonth() + 1;

    const title = `Hoteles en ${destinationName} del ${inDay} de ${inMonth} al ${outDay} de ${outMonth} | ${appName}`;
    const description = `Hoteles en ${destinationName} del ${inDay} de ${inMonth} al ${outDay} de ${outMonth}. Los mejores precios y servicios en ${appName}.`;

    return { title, description };
  }

  if (destinationName) {
    const title = `Hoteles en ${destinationName} | ${appName}`;
    const description = `Hoteles en ${destinationName}. Compará precios, ubicaciones y servicios en ${appName}. Reservá online.`;
    return { title, description };
  }

  return {
    title: `Buscar Hoteles | ${appName}`,
    description: `Encontrá los mejores hoteles en ${appName}. Compará precios, ubicaciones y servicios. Reservá online.`,
  };
};

const HotelListPage = () => {
  const { onSearchCount, resetOnSearchCount } = useGlobal();
  const { accionesAplicacion } = useCacheConfig();
  const { search } = useLocation();
  const appName = useAppName();
  const internalHotelsEnabled = useSelector(selectIsHotelSearcherEnabled);

  const externalHotelUrl = useMemo(() => {
    const hotelsAction = accionesAplicacion.find((accion) => accion?.name === 'hoteles');

    if (hotelsAction?.activo === true && hotelsAction?.url) {
      return hotelsAction.url;
    }

    return null;
  }, [accionesAplicacion]);

  const hasValidSearchParams = useMemo(() => {
    if (!search) return false;

    const searchParams = new URLSearchParams(search);
    const hasDestination = searchParams.get('destination');
    const hasCheckIn = searchParams.get('checkIn');
    const hasCheckOut = searchParams.get('checkOut');

    return hasDestination && hasCheckIn && hasCheckOut;
  }, [search]);

  const seoData = useMemo(() => buildSeoFromParams(search, appName), [search, appName]);

  useEffect(() => {
    if (!hasValidSearchParams && onSearchCount > 0) {
      resetOnSearchCount();
    }
  }, [hasValidSearchParams, onSearchCount, resetOnSearchCount]);

  useEffect(() => {
    if (!internalHotelsEnabled && externalHotelUrl && isClient) {
      window.location.assign(externalHotelUrl);
    }
  }, [externalHotelUrl, internalHotelsEnabled]);

  if (!internalHotelsEnabled) {
    return null;
  }

  const shouldShowResults = onSearchCount > 0 || hasValidSearchParams;

  return (
    <>
      <Seo {...seoData} />
      <SearchSelector isOnTargetPath />
      {shouldShowResults && <HotelResultsFromUrl />}
    </>
  );
};

export default HotelListPage;
